/**
 * FuelTab - Fuel transaction summary and commitment tracking
 *
 * Shows:
 * - Commitment progress bar (if lease has fuel commitment)
 * - Transient fuel summary (if transient stay, no commitment)
 * - Recent fuel transactions
 * - Monthly and YTD summary
 */

import { Fuel, TrendingUp } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useAircraftFuelSummary } from '@/hooks/useFuelTransactions'
import {
  formatFuelQuantity,
  formatFuelTotal,
  formatFuelDate,
  getDaysRemainingInMonth,
} from '@/types/fuel-transactions'
import { CompactInfoBlock } from './shared/CompactInfoBlock'
import { CommitmentProgress } from './shared/CommitmentProgress'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface FuelTabProps {
  data: AllocationEnrichedData
}

/**
 * Recent transactions list
 */
function RecentTransactions({
  transactions,
}: {
  transactions: Array<{
    id: string
    fueled_at: string
    quantity_gallons: number
    price_per_gallon: number
    total_amount: number
  }>
}) {
  if (!transactions || transactions.length === 0) {
    return (
      <CompactInfoBlock title="Recent Transactions">
        <p className="text-xs text-muted-foreground/50 text-center py-2">
          No recent fuel transactions
        </p>
      </CompactInfoBlock>
    )
  }

  return (
    <CompactInfoBlock title="Recent Transactions">
      <div className="space-y-1.5">
        {transactions.slice(0, 5).map((tx) => (
          <div
            key={tx.id}
            className="flex items-center justify-between text-xs py-1 border-b border-border/30 last:border-0"
          >
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {formatFuelDate(tx.fueled_at)}
              </span>
              <span className="font-mono">
                {formatFuelQuantity(tx.quantity_gallons)}
              </span>
            </div>
            <span className="font-medium">
              {formatFuelTotal(tx.total_amount)}
            </span>
          </div>
        ))}
      </div>
    </CompactInfoBlock>
  )
}

/**
 * Revenue summary component
 */
function RevenueSummary({
  monthlyGallons,
  monthlyRevenue,
  ytdGallons,
  ytdRevenue,
}: {
  monthlyGallons: number
  monthlyRevenue: number
  ytdGallons: number
  ytdRevenue: number
}) {
  return (
    <CompactInfoBlock title="Fuel Revenue">
      <div className="grid grid-cols-2 gap-3">
        {/* Monthly */}
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            This Month
          </div>
          <div className="text-sm font-semibold">
            {formatFuelTotal(monthlyRevenue)}
          </div>
          <div className="text-xs text-muted-foreground">
            {monthlyGallons.toLocaleString()} gal
          </div>
        </div>

        {/* YTD */}
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            YTD
          </div>
          <div className="text-sm font-semibold">
            {formatFuelTotal(ytdRevenue)}
          </div>
          <div className="text-xs text-muted-foreground">
            {ytdGallons.toLocaleString()} gal
          </div>
        </div>
      </div>
    </CompactInfoBlock>
  )
}

/**
 * Loading skeleton for FuelTab
 */
function FuelTabSkeleton() {
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-2 w-full" />
        <div className="flex justify-between">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-10" />
        </div>
      </div>
      <div className="border-t border-border/50" />
      <div className="space-y-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    </div>
  )
}

export function FuelTab({ data }: FuelTabProps) {
  const aircraftId = data.aircraft.id
  const contractId = data.leaseContract?.id

  // Fetch fuel summary for this aircraft
  const { data: fuelSummary, isLoading, error } = useAircraftFuelSummary(
    aircraftId,
    contractId
  )

  // Loading state
  if (isLoading) {
    return <FuelTabSkeleton />
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center min-h-[120px]">
        <Fuel className="w-8 h-8 text-destructive/30 mb-2" />
        <p className="text-sm text-destructive">
          Failed to load fuel data
        </p>
      </div>
    )
  }

  // No data state
  if (!fuelSummary) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center min-h-[120px]">
        <Fuel className="w-8 h-8 text-muted-foreground/30 mb-2" />
        <p className="text-sm text-muted-foreground">
          No fuel data available
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Fuel transactions will appear here
        </p>
      </div>
    )
  }

  const daysRemaining = getDaysRemainingInMonth()

  // Use real commitment from lease contract data if available
  const commitmentGallons = data.leaseContract?.fuelCommitmentGallonsMonthly
    ?? fuelSummary.commitmentGallonsMonthly
  const hasCommitment = !!commitmentGallons

  return (
    <div className="space-y-0">
      {/* Commitment Progress (Based aircraft with fuel commitment) */}
      {hasCommitment && commitmentGallons && (
        <>
          <CommitmentProgress
            committedGallons={commitmentGallons}
            deliveredGallons={fuelSummary.monthlyGallons}
            daysRemaining={daysRemaining}
            pricePerGallon={data.leaseContract?.fuelPricePerGallon ?? null}
            priceType={data.leaseContract?.fuelPriceType ?? null}
          />
          <div className="border-t border-border/50" />
        </>
      )}

      {/* No commitment badge (Based but no fuel commitment) */}
      {!hasCommitment && data.leaseContract && (
        <>
          <CompactInfoBlock>
            <Badge variant="outline" className="text-[10px]">
              No fuel commitment
            </Badge>
          </CompactInfoBlock>
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Transient fuel summary (no commitment, show what they've purchased this visit) */}
      {!hasCommitment && data.transientStay && fuelSummary.monthlyGallons > 0 && (
        <>
          <CompactInfoBlock title="Fuel This Visit">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <Fuel className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Purchased</span>
              </div>
              <span className="font-medium">
                {fuelSummary.monthlyGallons.toLocaleString()} gal
              </span>
            </div>
            {fuelSummary.monthlyRevenue > 0 && (
              <div className="flex items-center justify-between text-xs mt-1">
                <span className="text-muted-foreground">Revenue</span>
                <span className="font-medium">
                  {formatFuelTotal(fuelSummary.monthlyRevenue)}
                </span>
              </div>
            )}
          </CompactInfoBlock>
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Recent Transactions */}
      <RecentTransactions transactions={fuelSummary.recentTransactions} />

      <div className="border-t border-border/50" />

      {/* Revenue Summary */}
      <RevenueSummary
        monthlyGallons={fuelSummary.monthlyGallons}
        monthlyRevenue={fuelSummary.monthlyRevenue}
        ytdGallons={fuelSummary.ytdGallons}
        ytdRevenue={fuelSummary.ytdRevenue}
      />
    </div>
  )
}
